import { createSlice } from '@reduxjs/toolkit'
import { useSelector } from 'react-redux'
import { trimStart } from 'lodash-es'
import i18next from 'i18next'
import { useObjSelector } from 'utils'

// noinspection JSValidateTypes
/**
 * Holds the timezone used by restaurant-agnostic spots, like the date selector. Goes outside the state because it's
 * needed to set some parts of initialState.
 * This is either the timezone for the active restaurant, or the first in their list.
 * @see persistor (from _store.js)
 * @see setDefaultTimezone()
 * @type {?string}
 */
window.DEFAULT_TIMEZONE ||= null //might be empty on initial login

export function setDefaultTimezone(tz) { //TODO: should be moved to a Dates util file?
	window.DEFAULT_TIMEZONE = tz
	console.debug('Set default timezone', tz)
}

/**
 * Hey you! Looking for common project constants?
 * you're (luckily) in the wrong place. They're probably at 'utils/index'.
 * Now leave.
 */

/**
 * ********************* Yep, this is a dirty workaround ***********************
 *
 *   This was the best way I found to bring constants from the backend into the
 * frontend project in a blocking fashion - this way we are 100% sure these
 * constants are available whenever needed in the application.
 *   In production they are loaded statically by some code interpreted by the
 * PHP server (located at public/index.html), so the performance trouble
 * only happens in development - as you're not necessarily serving this from
 * PHP, but most times from the Webpack Dev Server.
 *   That's stored in Redux, so it's (almost) globally-accessible and still not
 * easy to mess with. If we simply left it as a window variable, it could be
 * tampered by injected code later on - "const" values are scoped to the <script>
 * they were defined. This way we are sure the constants are loaded before any
 * user-code could ever land the application, and is also stored in a safe box.
 *
 * ************************ I'm sorry if your eyes hurt ************************
 */

//TODO: since we're not depending on the state to keep the constants anymore (because it doesn't seem possible to update them later when stuff change, without an actual state change - just changing the initialState won't suffice with state persistence), I guess we can just strip this out from the state, and leave it in either localStorage or just in a global variable, in some sort of frozen state - but only if that's secure enough to not be tampered with after initial loading
const initialState = window.SERVER_CONSTANTS || (() => {
	console.info('blocking until we get the server constants!')

	const request = new XMLHttpRequest()
	request.open('GET', process.env.REACT_APP_API_PATH + 'constants', false)
	request.send()
	if (request.status == 200) {
		console.info('done! sorry for the wait :)')
		return Object.freeze(JSON.parse(request.responseText))
	} else {
		throw new Error('problems loading constants!')
	}
})()
const slice = createSlice({
	name        : 'constants',
	reducers    : {},
	initialState
})
export default slice.reducer //just so it's properly included in the store

/**
 * @typedef PRIVILEGES_LIST
 * @property {string} VIEW_ORDER
 * @property {string} ORDER Allows to create/edit orders
 * @property {string} CANCEL_ORDER Allows to cancel/delete orders
 * @property {string} CUSTOMER
 * @property {string} REPORTING
 * @property {string} PAYMENTINFO
 * @property {string} RMANAGER
 * @property {string} TABLET_ORDERS
 * @property {string} MENU_UPDATE
 * @property {string} BILLING_INFO
 */
/**
 * @typedef SERVER_CONSTANTS
 * @property {{
 *    SHIPDAY_STATUS: {
 *      DISABLED: int,
 *      ENABLED: int,
 *      REQUESTED: int,
 *      DISABLE_REQUESTED: int
 *    }
 *  }} ACCOUNT
 * @property {string[]} CHAT_PLATFORMS
 * @property {{ CUTLERY: string, NAPKINS: string }} CUSTOM_FIELDS
 * @property {{
 *    PAID_CREATOR: int,
 *    PAID_INDIVIDUALS: int
 *  }} GROUP_ORDER
 * @property {{
 *    ADJ_MAX_ITEM_CHARGE: int,
 *    ADJ_MAX_TIP_AMOUNT: int,
 *  }} ORDER
 * @property {{
 *    CREDIT: int,
 *    CUSTOM: int,
 *    DEBIT: int,
 *    HOUSE: int,
 *    OTHER: int,
 *    PAYPAL: int,
 *    PICKUP: int,
 *    PICKUP_CASH: int,
 *    PICKUP_CHECK: int,
 *    PICKUP_CREDIT: int,
 *    PICKUP_DEBIT: int,
 *    PREPAID: int,
 *    UNKNOWN_CARD: int,
 *    SYSTEM: int
 *  }} PAYMENT
 * @property {{CMO_IDS: int[]}} RESELLER
 * @property {PRIVILEGES_LIST} PRIVILEGES
 * }}
 */

function CONSTANT_OBJ(CONSTANTS) {

	/** @type SERVER_CONSTANTS */
	this.VALUES = CONSTANTS

	/**
	 * @param {Order} order
	 * @returns {string}
	 */
	this.paymentText = order => {
		const orderType = order.type.getName(true).toLowerCase()
		switch (order.payment_method) {
			case CONSTANTS.PAYMENT.CREDIT:
			case CONSTANTS.PAYMENT.DEBIT:
			case CONSTANTS.PAYMENT.PREPAID:
			case CONSTANTS.PAYMENT.UNKNOWN_CARD:
				switch (order.payment_status) {
					case 'PAID IN FULL':    return i18next.t('Paid online')
					case 'PROCESS PAYMENT': return i18next.t('Process payment', { context: 'card payment' })
					case 'COLLECT PAYMENT': return i18next.t('Collect payment', { context: 'card payment' })
					default:                return i18next.t('Card - check payment')
				}
			case CONSTANTS.PAYMENT.HOUSE : return i18next.t('House Account #{{number}}', { number: trimStart(order.house_account_id, '#') })
			case CONSTANTS.PAYMENT.PAYPAL: return i18next.t('PayPal')
			case CONSTANTS.PAYMENT.PICKUP: return i18next.t('Pay at {{orderType}}', { orderType })
			case CONSTANTS.PAYMENT.PICKUP_CASH  : return i18next.t('Pay at {{orderType}} with cash', { orderType })
			case CONSTANTS.PAYMENT.PICKUP_CHECK : return i18next.t('Pay at {{orderType}} with check', { orderType })
			case CONSTANTS.PAYMENT.PICKUP_CREDIT: return i18next.t('Pay at {{orderType}} with credit', { orderType })
			case CONSTANTS.PAYMENT.PICKUP_DEBIT : return i18next.t('Pay at {{orderType}} with debit/pre-paid card', { orderType })
			case CONSTANTS.PAYMENT.SYSTEM: return i18next.t('System Credit')
			case CONSTANTS.PAYMENT.CUSTOM: return order.custom_payment_type_name
			case CONSTANTS.PAYMENT.OTHER : return i18next.t('Other types of payment')
			case 'GROUP_CREATOR'         : return i18next.t('Payment by creator') //workaround as we can't use constants out of components yet...
			default: return i18next.t('Unrecognized payment method (#{{code}})', { code: order.payment_method })
		}
	}
}

/**
 * Hook for constants with general backend information.
 * @returns {CONSTANT_OBJ}
 */
export const useServerConstants = () => useSelector(getServerConstants)

/**
 * Only to be used inside other state operations! Otherwise, go with {@link useServerConstants}.
 * @return {CONSTANT_OBJ}
 */
export const getServerConstants = ({ constants }) => new CONSTANT_OBJ(constants)

/** @return PRIVILEGES_LIST */
export const PRIVILEGES = () => useObjSelector(({ constants }) => constants.PRIVILEGES)
